import { harvestKey } from './pack.js';
/**
 * The rule the player came closest to satisfying: fewest failing leaves, and on
 * a tie the higher-priority rule, which is the one the author put first.
 *
 * Rules with no failing leaves are impossible here — a rule with nothing false
 * in it would have fired — but they are filtered anyway rather than trusted,
 * because a rule that fails on a leaf the collector cannot locate would
 * otherwise be reported as the near miss and explain nothing.
 */
export function nearestMissIn(trace) {
    const missed = (trace?.missedRules ?? []).filter((rule) => rule.failingLeaves.length > 0);
    let nearest = null;
    for (const rule of missed) {
        if (nearest === null || rule.failingLeaves.length < nearest.failingLeaves.length) {
            nearest = rule;
        }
    }
    return nearest;
}
/** The nearest missed rule of the run's most recent resolution. */
export function nearestMissedRule(state) {
    return nearestMissIn(state.lastTrace);
}
/**
 * The axis the nearest missed rule failed on, or `null` where it failed on
 * something with no axis to name.
 *
 * Exported because the shell's feedback panel needs the same answer the hint
 * ladder's tier 2 gives, for a resolution that is not necessarily the run's
 * last one — and because the axis is the *only* part of a failure that may be
 * disclosed. The value on it never is, here or anywhere.
 */
export function failingAxisOf(trace) {
    return axisLeafOf(nearestMissIn(trace))?.axis ?? null;
}
/** Every slot the package declares, mission and persona alike, for its label. */
function slotDefOf(loaded, n) {
    const mission = loaded.pack.slots.find((slot) => slot.n === n);
    if (mission !== undefined)
        return mission;
    for (const puppet of loaded.pack.puppets) {
        const persona = puppet.personaSlots?.find((slot) => slot.n === n);
        if (persona !== undefined)
            return persona;
    }
    return undefined;
}
/**
 * Tier 1 — somewhere the player has not looked.
 *
 * Deliberately not "the particle you need is in #ops": the derived line points
 * at a *channel with unharvested content in it*, which is true whether or not
 * that content is on a winning path. Naming only channels the player can
 * already read keeps the hint actionable — pointing at a locked room would be
 * a puzzle rather than a hint.
 *
 * Messages are walked in ordinal order, so the line is the same for two runs
 * that reached the same state by different routes.
 */
function tier1(loaded, state) {
    for (const message of state.messages) {
        if (state.channels[message.channelId]?.unlocked !== true)
            continue;
        for (const highlight of message.highlights) {
            if (state.harvested.includes(harvestKey(message.id, highlight.id)))
                continue;
            const channel = loaded.channels[message.channelId];
            return {
                channelId: message.channelId,
                text: `There is still a phrase to collect in ${channel?.displayName ?? message.channelId}.`,
            };
        }
    }
    return null;
}
/** The first failing leaf that names an axis, which is what tier 2 reports. */
function axisLeafOf(rule) {
    return rule?.failingLeaves.find((leaf) => leaf.axis !== null) ?? null;
}
/**
 * The first failing leaf with a culprit in the assembly, and the earliest slot
 * that culprit occupies.
 *
 * A leaf can fail two ways and only one of them has a slot to blame: `has cost
 * zero` failed because *nothing* carried it, and there is no slot at fault;
 * `none tone aggressive` failed because something did, and that slot is exactly
 * what tier 3 names.
 */
function contributingSlotOf(rule) {
    for (const leaf of rule?.failingLeaves ?? []) {
        const slot = [...leaf.contributingSlots].sort((left, right) => left - right)[0];
        if (slot !== undefined)
            return slot;
    }
    return null;
}
/** How the node the message went to is referred to in a hint. */
function speakerOf(loaded, state) {
    const nodeId = state.lastTrace?.nodeId;
    if (nodeId === undefined)
        return 'They';
    return loaded.nodes[nodeId]?.displayName ?? nodeId;
}
/**
 * The line for one rung, and the same hint located for the interface.
 *
 * Always returns a hint: a tier with nothing to point at falls back to a line
 * that is still true and still discloses nothing, rather than to silence. A
 * player who has spent a hint is owed a sentence.
 */
export function deriveHint(loaded, state, tier) {
    const nearest = nearestMissedRule(state);
    const authored = nearest?.hintRefs ?? null;
    const derived = (() => {
        if (tier === 1) {
            const uncollected = tier1(loaded, state);
            return uncollected === null
                ? {
                    text: 'Everything worth collecting is already in your tray.',
                    channelId: null,
                    axis: null,
                    slot: null,
                }
                : { text: uncollected.text, channelId: uncollected.channelId, axis: null, slot: null };
        }
        if (tier === 2) {
            const leaf = axisLeafOf(nearest);
            const speaker = speakerOf(loaded, state);
            return leaf === null || leaf.axis === null
                ? {
                    text: `${speaker} did not react to your wording at all.`,
                    channelId: null,
                    axis: null,
                    slot: null,
                }
                : {
                    // The axis, never `leaf.value`: naming the value wanted on the axis
                    // would be stating the answer one particle short.
                    text: `${speaker} reacted to the ${leaf.axis} in that message, not to the idea.`,
                    channelId: null,
                    axis: leaf.axis,
                    slot: null,
                };
        }
        const slot = contributingSlotOf(nearest);
        if (slot === null) {
            return {
                text: 'None of your phrases was wrong. Something is missing.',
                channelId: null,
                axis: null,
                slot: null,
            };
        }
        const label = slotDefOf(loaded, slot)?.label;
        return {
            text: label === undefined
                ? `Look again at slot ${String(slot)}.`
                : `Look again at the ${label} slot.`,
            channelId: null,
            axis: null,
            slot,
        };
    })();
    // An authored override replaces the *line*, not the location: the shell still
    // highlights the channel, axis, or slot the derivation found, so a funnier
    // sentence never costs the player the affordance behind it.
    const override = tier === 1 ? authored?.tier1 : tier === 2 ? authored?.tier2 : authored?.tier3;
    return {
        tier,
        authored: override !== undefined,
        ...derived,
        text: override ?? derived.text,
    };
}
//# sourceMappingURL=hints.js.map